import React from 'react';
import styled from 'styled-components';
import { Tooltip } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';

const ChipRoot = styled.div`
    position: relative;
    display: inline-flex;
    align-items: center;
    max-width: min(100%, 280px);
    min-width: 0;
    box-sizing: border-box;
    padding: 8px 10px;
    padding-right: 10px;
    border-radius: 10px;
    border: 1px solid
        ${({ theme }) =>
            theme?.colors?.background?.default === '#141414' ? '#434343' : 'var(--ant-color-border-secondary, #d9d9d9)'};
    background: ${({ theme }) =>
        theme?.colors?.background?.default === '#141414'
            ? 'rgba(255, 255, 255, 0.04)'
            : 'var(--ant-color-fill-quaternary, rgba(0, 0, 0, 0.02))'};
    transition: padding-right 0.18s ease, box-shadow 0.18s ease;
    &:hover,
    &:focus-within {
        padding-right: 32px;
    }
`;

const ChipLabel = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: var(--ant-color-text);
    font-family: var(--font-family-geist);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
    line-height: 1.35;
`;

const ChipRemove = styled.button`
    position: absolute;
    right: 5px;
    top: 50%;
    transform: translateY(-50%) scale(0.92);
    width: 24px;
    height: 24px;
    padding: 0;
    border: none;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    color: var(--ant-color-text-tertiary);
    cursor: pointer;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.15s ease, transform 0.15s ease, color 0.15s ease, background 0.15s ease;
    ${ChipRoot}:hover &,
    ${ChipRoot}:focus-within & {
        opacity: 1;
        transform: translateY(-50%) scale(1);
        pointer-events: auto;
    }
    &:hover {
        color: var(--ant-color-text);
        background: ${({ theme }) =>
            theme?.colors?.background?.default === '#141414' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'};
    }
    &:focus-visible {
        outline: 2px solid var(--ant-color-primary);
        outline-offset: 1px;
    }
`;

/**
 * Compact chip: shows `label` only; `tooltipTitle` on hover; delete control appears on hover/focus-within (no idle spacer).
 */
export function HoverRevealRemoveChip({
    label = '—',
    tooltipTitle,
    onRemove,
    removeAriaLabel,
    mouseEnterDelay = 0.28,
    placement = 'top',
    ...rest
}) {
    return (
        <Tooltip
            title={tooltipTitle}
            mouseEnterDelay={mouseEnterDelay}
            placement={placement}
            overlayInnerStyle={{ maxWidth: 300, textAlign: 'left', color: '#ffffff' }}
            styles={{ body: { color: '#ffffff' } }}
        >
            <ChipRoot {...rest}>
                <ChipLabel>{label}</ChipLabel>
                <ChipRemove
                    type="button"
                    aria-label={removeAriaLabel || 'Remove'}
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove?.();
                    }}
                >
                    <DeleteOutlined style={{ fontSize: 12 }} />
                </ChipRemove>
            </ChipRoot>
        </Tooltip>
    );
}

HoverRevealRemoveChip.propTypes = {
    label: PropTypes.string,
    tooltipTitle: PropTypes.node,
    onRemove: PropTypes.func,
    removeAriaLabel: PropTypes.string,
    mouseEnterDelay: PropTypes.number,
    placement: PropTypes.string,
};

export default HoverRevealRemoveChip;
